/**
 * SITE PREVIEW BAR
 * Development tool to switch between different site configurations
 * Only shows in development mode - cleaner design
 */

import React, { useState } from 'react';
import { useSite } from '../contexts/SiteContext';
import { ChevronDown, ChevronUp, X } from 'lucide-react';

export const SitePreviewBar = () => {
  const { id, title, theme, allSites, switchSite } = useSite();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHidden, setIsHidden] = useState(false);

  // Only show in development
  if (process.env.NODE_ENV === 'production' || isHidden) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Collapsed View */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-full shadow-lg text-white text-sm font-medium"
          style={{ backgroundColor: theme.primaryHex }}
        >
          <span 
            className="w-3 h-3 rounded-full bg-white"
          />
          Preview: {title.split('.')[0]}
          <ChevronUp className="w-4 h-4" />
        </button>
      )}

      {/* Expanded View */}
      {isExpanded && (
        <div className="bg-white rounded-xl shadow-2xl p-4 min-w-[280px] border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-gray-700">Switch Site Preview</span>
            <div className="flex gap-1">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
              <button
                onClick={() => setIsHidden(true)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
          
          <div className="space-y-2">
            {Object.values(allSites).map((site) => (
              <button
                key={site.id}
                onClick={() => {
                  switchSite(site.id);
                  setIsExpanded(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                  site.id === id
                    ? 'ring-2 ring-offset-1'
                    : 'hover:bg-gray-50'
                }`}
                style={site.id === id ? { 
                  backgroundColor: `${site.theme.primaryHex}15`,
                  ringColor: site.theme.primaryHex 
                } : {}}
                data-testid={`switch-site-${site.id}`}
              >
                <span 
                  className="w-4 h-4 rounded-full flex-shrink-0"
                  style={{ backgroundColor: site.theme.primaryHex }}
                />
                <div>
                  <p className="font-medium text-gray-900 text-sm">
                    {site.bodyPart || site.title}
                  </p>
                  <p className="text-xs text-gray-500">{site.domain}</p>
                </div>
                {site.id === id && (
                  <span className="ml-auto text-xs font-medium" style={{ color: site.theme.primaryHex }}>
                    Active
                  </span>
                )}
              </button>
            ))}
          </div>
          
          <p className="text-xs text-gray-400 mt-3 text-center">
            This panel only shows in development
          </p>
        </div>
      )}
    </div>
  );
};
